import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tile } from "./Tile";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface TutorialProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Tutorial({ isOpen, onClose }: TutorialProps) {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "Welcome to WordPop!",
      content: (
        <div className="text-center space-y-4">
          <p>Guess the 5-letter word in 6 tries or less!</p>
          <p>Each guess must be a valid word.</p>
        </div>
      ),
    },
    {
      title: "How Colors Work",
      content: (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Tile char="W" status="correct" />
            <span className="text-sm">Pink: Letter is in the correct position</span>
          </div>
          <div className="flex items-center gap-2">
            <Tile char="O" status="present" />
            <span className="text-sm">Purple: Letter is in the word but wrong position</span>
          </div>
          <div className="flex items-center gap-2">
            <Tile char="R" status="absent" />
            <span className="text-sm">Gray: Letter is not in the word</span>
          </div>
        </div>
      ),
    },
    {
      title: "Example",
      content: (
        <div className="space-y-4">
          <p className="text-sm text-foreground/70">If the word is "HOUSE":</p>
          <div className="flex gap-1 justify-center">
            <Tile char="H" status="correct" />
            <Tile char="E" status="absent" />
            <Tile char="L" status="absent" />
            <Tile char="L" status="absent" />
            <Tile char="O" status="present" />
          </div>
          <div className="text-sm space-y-2">
            <p>• H is in the correct position</p>
            <p>• O is in the word but wrong position</p>
            <p>• E, L, L are not in the word</p>
          </div>
        </div>
      ),
    },
    {
      title: "Hard Mode",
      content: (
        <div className="space-y-3 text-sm">
          <p>• <strong>Hard Mode:</strong> More challenging gameplay!</p>
          <p>• Any revealed hints must be used in your subsequent guesses</p>
          <p>• Cannot reuse letters that were marked as incorrect (gray)</p>
          <p>• For example, if a hint reveals the word starts with "S", all your future guesses must start with "S"</p>
          <p>• Toggle hard mode before starting a new game (can't change mid-game)</p>
        </div>
      ),
    },
    {
      title: "Tips & Features",
      content: (
        <div className="space-y-3 text-sm">
          <p>• Use the hint button if you get stuck (3 hints max)</p>
          <p>• Switch between light and dark themes</p>
          <p>• Works on both desktop and mobile</p>
          <p>• Use your physical keyboard or tap the on-screen keys</p>
        </div>
      ),
    },
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md animate-fade-in" aria-describedby="tutorial-description">
        <DialogHeader>
          <DialogTitle>
            {steps[currentStep].title}
          </DialogTitle>
        </DialogHeader>
        
        <div id="tutorial-description" className="py-4">
          {steps[currentStep].content}
        </div>

        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={prevStep}
            disabled={currentStep === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>

          <div className="flex gap-1">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentStep ? "bg-primary" : "bg-muted"
                }`}
                aria-label={`Step ${index + 1} of ${steps.length}${index === currentStep ? ' (current)' : ''}`}
                role="progressbar"
                aria-valuenow={index === currentStep ? 1 : 0}
                aria-valuemin={0}
                aria-valuemax={1}
              />
            ))}
          </div>

          {currentStep < steps.length - 1 ? (
            <Button size="sm" onClick={nextStep}>
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button size="sm" onClick={handleClose}>
              Let's Play!
            </Button>
          )}
        </div>

        <div className="mt-4 pt-4 border-t text-center">
          <Link 
            to="/help" 
            onClick={handleClose}
            className="text-sm text-primary hover:underline inline-flex items-center gap-1"
          >
            Read Full Help Guide →
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}